import React, { useState } from 'react';
import { PUBLISHING_DOMAINS_DATA, DomainCategoryData, DomainCapabilitySpec } from '../data/companyData';
import {
  GraduationCap,
  BookOpen,
  ShieldCheck,
  CheckCircle2,
  ChevronRight,
  ChevronLeft,
  Check,
  Award,
  Layers,
  Sparkles,
  Cpu,
  FileCheck,
  FileCode2,
  Sliders,
  CheckSquare,
  Shield
} from 'lucide-react';

export const TestimonialsSection: React.FC = () => {
  const [activeCategoryId, setActiveCategoryId] = useState<'higher-education' | 'k12-education' | 'interactive-accessibility'>('higher-education');
  const [selectedSpecIndex, setSelectedSpecIndex] = useState<number>(0);

  const activeCategory: DomainCategoryData =
    PUBLISHING_DOMAINS_DATA.find((c) => c.id === activeCategoryId) || PUBLISHING_DOMAINS_DATA[0];

  const currentSpec: DomainCapabilitySpec =
    activeCategory.specifications[selectedSpecIndex] || activeCategory.specifications[0];

  const handleCategoryChange = (categoryId: 'higher-education' | 'k12-education' | 'interactive-accessibility') => {
    setActiveCategoryId(categoryId);
    setSelectedSpecIndex(0);
  };

  const getCategoryIcon = (iconName: string) => {
    switch (iconName) {
      case 'GraduationCap':
        return <GraduationCap className="w-5 h-5" />;
      case 'BookOpen':
        return <BookOpen className="w-5 h-5" />;
      case 'ShieldCheck':
        return <ShieldCheck className="w-5 h-5" />;
      default:
        return <Layers className="w-5 h-5" />;
    }
  };

  return (
    <section
      id="testimonials"
      className="py-16 sm:py-24 bg-slate-900 text-white relative overflow-hidden"
      role="region"
      aria-label="Domain Expertise & Technical Specifications"
    >
      {/* Background Lighting Accent */}
      <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[900px] h-[500px] bg-blue-600/10 blur-3xl rounded-full pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        
        {/* Section Header */}
        <div className="text-center space-y-4 max-w-3xl mx-auto">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-blue-500/20 border border-blue-400/30 text-blue-300 text-xs font-bold uppercase tracking-wider">
            <Sparkles className="w-4 h-4 text-blue-400" /> Publishing Divisions & Technical Capabilities
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white tracking-tight">
            Core Publishing Divisions & Production Standards
          </h2>
          <p className="text-slate-300 text-sm sm:text-base leading-relaxed">
            Explore our specialized workflows, technical specifications, quality standards, and output deliverables across our 3 core publishing categories.
          </p>
        </div>

        {/* 3 Core Domain Category Tabs */}
        <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-4 max-w-5xl mx-auto">
          {PUBLISHING_DOMAINS_DATA.map((cat) => {
            const isActive = activeCategoryId === cat.id;
            return (
              <button
                key={cat.id}
                onClick={() => handleCategoryChange(cat.id)}
                className={`p-5 rounded-2xl border text-left transition-all duration-300 flex flex-col justify-between gap-3 relative overflow-hidden group ${
                  isActive
                    ? 'bg-slate-800 text-white border-blue-500 shadow-xl ring-2 ring-blue-500/50'
                    : 'bg-slate-800/40 text-slate-300 border-slate-700/80 hover:bg-slate-800/80 hover:border-slate-600'
                }`}
              >
                {isActive && (
                  <div className="absolute top-0 right-0 w-28 h-28 bg-blue-500/10 rounded-full blur-xl pointer-events-none" />
                )}

                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div className={`p-2.5 rounded-xl border ${cat.accentColor} shrink-0`}>
                    {getCategoryIcon(cat.iconName)}
                  </div>
                  <span
                    className={`text-[10px] font-mono font-extrabold px-2.5 py-0.5 rounded-full border shrink-0 whitespace-nowrap ${
                      isActive ? 'bg-blue-500/20 text-blue-300 border-blue-400/40' : 'bg-slate-700/50 text-slate-400 border-slate-600'
                    }`}
                  >
                    {cat.badge}
                  </span>
                </div>

                <div>
                  <h3 className="text-lg font-black text-white group-hover:text-blue-300 transition-colors">
                    {cat.title}
                  </h3>
                  <p className="text-xs text-slate-300 line-clamp-2 mt-1 leading-snug">
                    {cat.tagline}
                  </p>
                </div>

                <div className="pt-2 border-t border-slate-700/50 flex items-center justify-between text-[11px] text-slate-300 font-semibold">
                  <span className="flex items-center gap-1">
                    <Award className="w-3.5 h-3.5 text-blue-400" />
                    {cat.specifications.length} Technical Modules
                  </span>
                  <span className={`flex items-center gap-0.5 ${isActive ? 'text-blue-300 font-bold' : 'text-slate-300 font-medium group-hover:text-white'}`}>
                    <span>View Specifications</span>
                    <ChevronRight className="w-3.5 h-3.5" />
                  </span>
                </div>
              </button>
            );
          })}
        </div>

        {/* Selected Domain Statistics & SLA Banner */}
        <div className="mt-8 p-4 sm:p-5 rounded-2xl bg-gradient-to-r from-slate-800 via-slate-800/90 to-slate-800/80 border border-slate-700/80 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className={`p-3 rounded-xl border shrink-0 ${activeCategory.accentColor}`}>
              {getCategoryIcon(activeCategory.iconName)}
            </div>
            <div>
              <span className="text-xs font-bold uppercase tracking-wider text-blue-400 block">
                Active Division Target
              </span>
              <h4 className="text-lg font-black text-white">
                {activeCategory.title}
              </h4>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-6 text-center md:text-right text-xs w-full md:w-auto">
            <div className="bg-slate-900/60 p-2.5 px-3.5 rounded-xl border border-slate-700/60">
              <span className="text-[10px] text-slate-400 block font-mono">Capacity Benchmark</span>
              <span className="font-extrabold text-blue-300 text-xs sm:text-sm">{activeCategory.volumeCapacity}</span>
            </div>
            <div className="bg-slate-900/60 p-2.5 px-3.5 rounded-xl border border-slate-700/60">
              <span className="text-[10px] text-slate-400 block font-mono">Accuracy SLA</span>
              <span className="font-extrabold text-emerald-400 text-xs sm:text-sm">{activeCategory.slaBenchmark}</span>
            </div>
            <div className="bg-slate-900/60 p-2.5 px-3.5 rounded-xl border border-slate-700/60">
              <span className="text-[10px] text-slate-400 block font-mono">Compliance Standard</span>
              <span className="font-extrabold text-purple-300 text-xs sm:text-sm">{activeCategory.qualityStandard}</span>
            </div>
          </div>
        </div>

        {/* Main Detailed Technical Capability Card */}
        <div className="mt-8 bg-slate-800/90 rounded-3xl p-6 sm:p-10 border border-slate-700/80 shadow-2xl relative overflow-hidden">
          
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
            
            {/* Left Column: Specification Overview & Tools (4 cols) */}
            <div className="lg:col-span-4 space-y-5 bg-slate-900/60 p-6 rounded-2xl border border-slate-700/60">
              <div className="space-y-2">
                <span className="text-[10px] font-mono uppercase text-blue-400 font-bold block">
                  Module #{selectedSpecIndex + 1} Specification
                </span>
                <h4 className="text-lg font-black text-white leading-snug">
                  {currentSpec.subTitle}
                </h4>
                <p className="text-xs text-slate-300 leading-relaxed pt-1">
                  {currentSpec.scopeDescription}
                </p>
              </div>

              <div className="pt-4 border-t border-slate-800 space-y-2">
                <span className="text-[10px] font-mono uppercase text-slate-400 block">Production SLA Benchmark</span>
                <div className="p-3 rounded-xl bg-emerald-950/60 border border-emerald-800/60 flex items-center gap-2">
                  <Shield className="w-4 h-4 text-emerald-400 shrink-0" />
                  <span className="text-xs font-bold text-emerald-300">{currentSpec.slaBenchmark}</span>
                </div>
              </div>

              <div className="space-y-2">
                <span className="text-[10px] font-mono uppercase text-slate-400 block">Software & Production Stack</span>
                <div className="flex flex-wrap gap-1.5">
                  {currentSpec.softwareStack.map((tool, idx) => (
                    <span
                      key={idx}
                      className="px-2.5 py-1 rounded-lg bg-slate-800 text-slate-300 text-[10px] font-mono font-semibold border border-slate-700 flex items-center gap-1"
                    >
                      <Cpu className="w-3 h-3 text-blue-400" />
                      {tool}
                    </span>
                  ))}
                </div>
              </div>

              <div className="pt-2">
                <span className="text-[10px] font-mono uppercase text-slate-400 block mb-2">Quality Guarantees</span>
                <ul className="space-y-1.5 text-xs text-slate-300">
                  {currentSpec.qualityStandards.map((std, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <CheckSquare className="w-3.5 h-3.5 text-blue-400 shrink-0 mt-0.5" />
                      <span>{std}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Right Column: Capabilities & Output Deliverables (8 cols) */}
            <div className="lg:col-span-8 space-y-6">
              
              <div>
                <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-blue-500/20 text-blue-300 text-[11px] font-bold border border-blue-400/30 mb-2">
                  <FileCheck className="w-3.5 h-3.5" /> Active Technical Workflow Breakdown
                </div>
                <h3 className="text-xl sm:text-2xl font-black text-white leading-snug">
                  {currentSpec.subTitle} Capabilities
                </h3>
              </div>

              {/* Grid of Capabilities & Deliverables */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                
                {/* Technical Capabilities */}
                <div className="p-5 rounded-2xl bg-slate-900/50 border border-slate-700/60 space-y-3">
                  <div className="flex items-center gap-2 text-blue-400 font-bold text-sm border-b border-slate-800 pb-2">
                    <Sliders className="w-4 h-4" />
                    <span>Technical Capabilities & Workflows</span>
                  </div>
                  <ul className="space-y-2.5 text-xs text-slate-300">
                    {currentSpec.technicalCapabilities.map((cap, i) => (
                      <li key={i} className="flex items-start gap-2.5 leading-relaxed">
                        <CheckCircle2 className="w-4 h-4 text-blue-400 shrink-0 mt-0.5" />
                        <span>{cap}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Output Deliverables */}
                <div className="p-5 rounded-2xl bg-slate-900/50 border border-slate-700/60 space-y-3">
                  <div className="flex items-center gap-2 text-emerald-400 font-bold text-sm border-b border-slate-800 pb-2">
                    <FileCode2 className="w-4 h-4" />
                    <span>Output Deliverables & File Formats</span>
                  </div>
                  <ul className="space-y-2.5 text-xs text-slate-300">
                    {currentSpec.outputDeliverables.map((deliv, i) => (
                      <li key={i} className="flex items-start gap-2.5 leading-relaxed">
                        <Check className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />
                        <span>{deliv}</span>
                      </li>
                    ))}
                  </ul>
                </div>

              </div>

              {/* Module Switcher within the Active Domain */}
              <div className="pt-4 border-t border-slate-700/80 flex flex-col sm:flex-row items-center justify-between gap-4">
                <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
                  {activeCategory.specifications.map((spec, idx) => (
                    <button
                      key={spec.id}
                      onClick={() => setSelectedSpecIndex(idx)}
                      className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 whitespace-nowrap shrink-0 ${
                        selectedSpecIndex === idx
                          ? 'bg-blue-600 text-white shadow-md'
                          : 'bg-slate-700/60 text-slate-400 hover:bg-slate-700 hover:text-white'
                      }`}
                    >
                      <span>Module #{idx + 1}</span>
                      {selectedSpecIndex === idx && <CheckCircle2 className="w-3.5 h-3.5 text-white shrink-0" />}
                    </button>
                  ))}
                </div>

                <div className="flex items-center gap-2 shrink-0">
                  <button
                    onClick={() =>
                      setSelectedSpecIndex(
                        (prev) => (prev - 1 + activeCategory.specifications.length) % activeCategory.specifications.length
                      )
                    }
                    className="p-2 rounded-xl bg-slate-700 hover:bg-slate-600 text-white transition-colors text-xs font-semibold flex items-center gap-1 whitespace-nowrap"
                  >
                    <ChevronLeft className="w-4 h-4 shrink-0" />
                    <span>Previous</span>
                  </button>
                  <button
                    onClick={() =>
                      setSelectedSpecIndex((prev) => (prev + 1) % activeCategory.specifications.length)
                    }
                    className="p-2 rounded-xl bg-slate-700 hover:bg-slate-600 text-white transition-colors text-xs font-semibold flex items-center gap-1 whitespace-nowrap"
                  >
                    <span>Next Module</span>
                    <ChevronRight className="w-4 h-4 shrink-0" />
                  </button>
                </div>
              </div>

            </div>

          </div>
        </div>

        {/* All Capability Modules Grid in Selected Domain */}
        <div className="mt-12 space-y-4">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <h3 className="text-xl font-extrabold text-white flex items-center gap-2">
              <Layers className="w-5 h-5 text-blue-400 shrink-0" />
              <span>All Technical Modules for {activeCategory.title}</span>
            </h3>
            <span className="text-xs text-slate-400 font-mono whitespace-nowrap">
              {activeCategory.specifications.length} Production Specifications Available
            </span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {activeCategory.specifications.map((spec, idx) => {
              const isSelected = selectedSpecIndex === idx;

              return (
                <div
                  key={spec.id}
                  onClick={() => setSelectedSpecIndex(idx)}
                  className={`p-5 rounded-2xl border transition-all cursor-pointer flex flex-col justify-between gap-4 ${
                    isSelected
                      ? 'bg-slate-800 text-white border-blue-500 shadow-xl ring-2 ring-blue-500/30'
                      : 'bg-slate-800/50 text-slate-300 border-slate-700/80 hover:bg-slate-800/80 hover:border-slate-600'
                  }`}
                >
                  <div className="space-y-3">
                    <div className="space-y-1.5 mb-1">
                      <div>
                        <span className="px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-blue-500/20 text-blue-300 border border-blue-400/30 inline-block">
                          Module #{idx + 1}
                        </span>
                      </div>
                      <div>
                        <span className="text-[10px] font-mono text-emerald-400 font-extrabold bg-emerald-950/40 px-2.5 py-0.5 rounded-full border border-emerald-800/40 inline-block">
                          {spec.slaBenchmark}
                        </span>
                      </div>
                    </div>

                    <h4 className="text-sm font-bold text-white line-clamp-2 leading-snug pt-0.5">
                      {spec.subTitle}
                    </h4>

                    <p className="text-xs text-slate-400 line-clamp-3 leading-relaxed">
                      {spec.scopeDescription}
                    </p>
                  </div>

                  <div className="pt-3 border-t border-slate-700/60">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedSpecIndex(idx);
                      }}
                      className="w-full py-1.5 px-3 rounded-xl bg-slate-900/80 hover:bg-slate-900 text-blue-400 hover:text-blue-300 text-xs font-bold transition-colors flex items-center justify-center gap-1 border border-slate-700/60"
                    >
                      <span>{isSelected ? 'Currently Viewing' : 'Select Technical Module'}</span>
                      <ChevronRight className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

      </div>
    </section>
  );
};
